/**
 * Slug Generation Utilities
 * Generates human-readable, URL-safe slugs for boards
 *
 * @module utils/slug
 */

/**
 * Common stop words to remove from slugs for cleaner URLs
 * Based on SEO best practices: keep slugs to 3-5 meaningful words
 */
const STOP_WORDS = new Set([
    'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
    'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'been',
    'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
    'should', 'may', 'might', 'must', 'shall', 'can', 'need', 'dare', 'ought',
    'used', 'my', 'your', 'his', 'her', 'its', 'our', 'their', 'this', 'that',
    'these', 'those', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 'what',
    'which', 'who', 'whom', 'when', 'where', 'why', 'how', 'all', 'each',
    'every', 'both', 'few', 'more', 'most', 'other', 'some', 'such', 'no',
    'nor', 'not', 'only', 'own', 'same', 'so', 'than', 'too', 'very', 'just'
]);

/**
 * Generates a URL-friendly slug from a title
 * Handles edge cases: special characters, unicode, multiple spaces, etc.
 * Removes common stop words for cleaner, SEO-friendly URLs
 *
 * @param title - The title to convert to a slug
 * @returns A URL-safe slug string
 *
 * @example
 * generateSlug("My Awesome Moodboard!") // "awesome-moodboard"
 * generateSlug("The Ultimate Guide to Design") // "ultimate-guide-design"
 * generateSlug("Project 2024 - Q1")     // "project-2024-q1"
 */
export function generateSlug(title: string): string {
    if (!title || typeof title !== 'string') {
        return '';
    }

    const processed = title
        .toLowerCase()
        .trim()
        // Normalize unicode characters (é → e, ñ → n, etc.)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        // Replace special characters with spaces (keep alphanumerics, spaces, hyphens)
        .replace(/[^\w\s-]/g, ' ')
        // Split into words
        .split(/[\s_-]+/)
        // Remove stop words (but keep at least one word)
        .filter((word, _, arr) => {
            const meaningfulWords = arr.filter(w => !STOP_WORDS.has(w) && w.length > 0);
            // If all words are stop words, keep them all
            if (meaningfulWords.length === 0) return word.length > 0;
            // Otherwise filter out stop words
            return !STOP_WORDS.has(word) && word.length > 0;
        })
        // Join with hyphens
        .join('-')
        // Replace multiple consecutive hyphens with single hyphen
        .replace(/-+/g, '-')
        // Remove hyphens from start and end
        .replace(/^-+|-+$/g, '')
        // Limit length to prevent excessively long URLs
        .substring(0, 100);

    return processed;
}

/**
 * Generates a unique slug by appending a numeric suffix if collision detected
 *
 * @param title - The title to convert to a slug
 * @param existingSlugs - Array of already-used slugs to check against
 * @returns A unique slug string
 *
 * @example
 * generateUniqueSlug("My Board", ["my-board"]) // "my-board-1"
 * generateUniqueSlug("My Board", ["my-board", "my-board-1"]) // "my-board-2"
 */
export function generateUniqueSlug(title: string, existingSlugs: string[]): string {
    let baseSlug = generateSlug(title);

    // Fallback for empty or invalid titles
    if (!baseSlug) {
        baseSlug = 'untitled';
    }

    // If slug doesn't exist, return as-is
    if (!existingSlugs.includes(baseSlug)) {
        return baseSlug;
    }

    // Find unique suffix
    let counter = 1;
    let candidateSlug = `${baseSlug}-${counter}`;

    while (existingSlugs.includes(candidateSlug)) {
        counter++;
        candidateSlug = `${baseSlug}-${counter}`;

        // Safety limit to prevent infinite loops (shouldn't ever hit this)
        if (counter > 1000) {
            // Append timestamp as fallback
            candidateSlug = `${baseSlug}-${Date.now()}`;
            break;
        }
    }

    return candidateSlug;
}

/**
 * Validates a slug format
 * Ensures slug meets URL requirements
 *
 * @param slug - The slug to validate
 * @returns true if slug is valid, false otherwise
 */
export function isValidSlug(slug: string): boolean {
    if (!slug || typeof slug !== 'string') {
        return false;
    }

    // Slug must be 1-100 characters, lowercase alphanumeric and hyphens only
    // Cannot start or end with hyphen
    const slugRegex = /^[a-z0-9][a-z0-9-]{0,98}[a-z0-9]$|^[a-z0-9]$/;
    return slugRegex.test(slug);
}

/**
 * Sanitizes user-provided custom slug
 * Cleans up a user-entered slug to meet format requirements
 *
 * @param input - User-provided slug input
 * @returns Sanitized slug string
 */
export function sanitizeSlug(input: string): string {
    if (!input || typeof input !== 'string') {
        return '';
    }

    return input
        .toLowerCase()
        .trim()
        // Remove any character that isn't alphanumeric or hyphen
        .replace(/[^a-z0-9-]/g, '')
        // Replace multiple hyphens with single
        .replace(/-+/g, '-')
        // Remove leading/trailing hyphens
        .replace(/^-+|-+$/g, '')
        // Limit length
        .substring(0, 100);
}
